import React from "react"

export function resolveClinicLabel(rows, clinicId) {
  if (!clinicId || !Array.isArray(rows) || rows.length === 0) return ""

  const match = rows.find((row) => {
    const directId =
      row?.clinicId || row?.id || row?.clinic?.id || row?.membership?.clinicId
    return directId === clinicId
  })

  if (!match) return ""

  return (
    match?.clinicName ||
    match?.name ||
    match?.clinic?.name ||
    match?.membership?.clinicName ||
    "Clínica seleccionada"
  )
}

export function humanRoleLabel(role) {
  const upper = String(role || "").toUpperCase()
  if (upper === "ADMIN") return "Administrador"
  if (upper === "DOCTOR") return "Doctor"
  if (upper === "RECEPTIONIST") return "Recepcionista"
  if (upper === "ASSISTANT") return "Asistente"
  if (upper === "FINANCE") return "Financiero"
  if (upper === "STAFF") return "Staff"
  return role || "Sin rol"
}

export function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

export function humanSpecialtyLabel(value) {
  const upper = String(value || "").toUpperCase()
  if (!upper) return "Sin especialidad"
  if (upper === "GENERAL") return "General"
  if (upper === "ORTODONCIA") return "Ortodoncia"
  if (upper === "ENDODONCIA") return "Endodoncia"
  if (upper === "CIRUGIA") return "Cirugía"
  if (upper === "IMPLANTOLOGIA") return "Implantología"
  if (upper === "PROSTODONCIA") return "Prostodoncia"
  if (upper === "PERIODONCIA") return "Periodoncia"
  if (upper === "PEDIATRIA") return "Odontopediatría"
  return value || "Sin especialidad"
}

export function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              marginTop: 4,
              fontSize: 12.5,
              color: "#64748b",
              lineHeight: 1.45
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

export function FieldBlock({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 800,
          marginBottom: 6
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

export function MetricCard({ label, value, helper, tone = "neutral" }) {
  const palette = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", value: "#991b1b" },
    info: { bg: "#eff6ff", border: "#bfdbfe", value: "#1d4ed8" }
  }

  const visual = palette[tone] || palette.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 20,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.1,
          wordBreak: "break-word"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            marginTop: 5,
            fontSize: 12.5,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

export function InsightCard({
  title,
  status,
  tone = "info",
  detail,
  ctaLabel,
  onClick
}) {
  const palette = {
    success: { bg: "#f0fdf4", border: "#bbf7d0", status: "#166534" },
    warning: { bg: "#fffbeb", border: "#fde68a", status: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", status: "#991b1b" },
    info: { bg: "#eff6ff", border: "#bfdbfe", status: "#1d4ed8" }
  }

  const visual = palette[tone] || palette.info

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
        display: "grid",
        gap: 10
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          {title}
        </div>

        <div
          style={{
            display: "inline-flex",
            padding: "4px 8px",
            borderRadius: 999,
            background: visual.bg,
            border: `1px solid ${visual.border}`,
            color: visual.status,
            fontSize: 11,
            fontWeight: 900
          }}
        >
          {status}
        </div>
      </div>

      <div
        style={{
          fontSize: 13,
          color: "#64748b",
          lineHeight: 1.45
        }}
      >
        {detail}
      </div>

      <div>
        <button onClick={onClick} style={cardButtonStyle}>
          {ctaLabel}
        </button>
      </div>
    </div>
  )
}

export function TopStateCard({ tone = "warning", title, children }) {
  const palette = {
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" },
    info: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
  }

  const visual = palette[tone] || palette.info

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}

export function InlineNotice({ tone = "warning", children }) {
  const palette = {
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
    info: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
  }

  const visual = palette[tone] || palette.info

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 12,
        padding: 12,
        lineHeight: 1.4
      }}
    >
      {children}
    </div>
  )
}

export function EmptyState({ text }) {
  return (
    <div
      style={{
        border: "1px dashed #e2e8f0",
        background: "#f8fafc",
        color: "#64748b",
        borderRadius: 14,
        padding: 16
      }}
    >
      {text}
    </div>
  )
}

export function ListItem({ title, metaLeft, metaRight, extra = "" }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff",
        padding: 14
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.2fr) auto auto",
          gap: 12,
          alignItems: "center"
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 900,
              color: "#0f172a"
            }}
          >
            {title}
          </div>

          {metaLeft ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 12.5,
                color: "#64748b",
                lineHeight: 1.4,
                wordBreak: "break-word"
              }}
            >
              {metaLeft}
            </div>
          ) : null}
        </div>

        <div style={softPillStyle}>{metaRight}</div>
        {extra ? <div style={softPillStyle}>{extra}</div> : <div />}
      </div>
    </div>
  )
}

export function EditableListItem({
  title,
  metaLeft,
  metaRight,
  extra = "",
  onEdit
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff",
        padding: 14
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.2fr) auto auto auto",
          gap: 12,
          alignItems: "center"
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 900,
              color: "#0f172a"
            }}
          >
            {title}
          </div>

          {metaLeft ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 12.5,
                color: "#64748b",
                lineHeight: 1.4,
                wordBreak: "break-word"
              }}
            >
              {metaLeft}
            </div>
          ) : null}
        </div>

        <div style={softPillStyle}>{metaRight}</div>
        {extra ? <div style={softPillStyle}>{extra}</div> : <div />}
        <button onClick={onEdit} style={editButtonStyle}>
          Editar
        </button>
      </div>
    </div>
  )
}

export function EditModalShell({
  title,
  subtitle,
  onClose,
  actions,
  children
}) {
  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div>
            <div style={modalTitleStyle}>{title}</div>
            {subtitle ? <div style={modalSubtitleStyle}>{subtitle}</div> : null}
          </div>

          <button onClick={onClose} style={closeButtonStyle}>
            ×
          </button>
        </div>

        {children}

        {actions ? <div style={modalActionsStyle}>{actions}</div> : null}
      </div>
    </div>
  )
}

export const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#ffffff",
  boxSizing: "border-box"
}

export const pillStyle = {
  display: "inline-flex",
  minHeight: 38,
  alignItems: "center",
  padding: "8px 10px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#334155",
  fontWeight: 800,
  fontSize: 12
}

export const softPillStyle = {
  display: "inline-flex",
  padding: "6px 9px",
  borderRadius: 999,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#475569",
  fontWeight: 800,
  fontSize: 12,
  whiteSpace: "nowrap"
}

export const listStyle = {
  display: "grid",
  gap: 10
}

export const permissionsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 10
}

export const checkboxCardStyle = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  cursor: "pointer"
}

export const checkboxTitleStyle = {
  fontSize: 14,
  fontWeight: 800,
  color: "#0f172a",
  lineHeight: 1.3
}

export const checkboxMetaStyle = {
  marginTop: 4,
  fontSize: 12.5,
  color: "#64748b",
  lineHeight: 1.35
}

export const actionButtonStyle = {
  minHeight: 36,
  padding: "8px 12px",
  borderRadius: 999,
  cursor: "pointer",
  fontWeight: 900,
  fontSize: 12
}

export const cardButtonStyle = {
  minHeight: 36,
  padding: "8px 12px",
  borderRadius: 12,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 12
}

export const primaryButtonStyle = {
  minHeight: 40,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontWeight: 800,
  fontSize: 13
}

export const secondaryButtonStyle = {
  minHeight: 40,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer"
}

export const editButtonStyle = {
  minHeight: 34,
  padding: "7px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
  fontWeight: 800,
  fontSize: 12,
  cursor: "pointer"
}

export const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.48)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 1200
}

export const modalCardStyle = {
  width: "min(760px, 100%)",
  background: "#ffffff",
  borderRadius: 20,
  border: "1px solid #e2e8f0",
  boxShadow: "0 24px 80px rgba(15,23,42,0.24)",
  padding: 18
}

export const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
  marginBottom: 14
}

export const modalTitleStyle = {
  fontSize: 18,
  fontWeight: 900,
  color: "#0f172a"
}

export const modalSubtitleStyle = {
  marginTop: 4,
  fontSize: 12.5,
  color: "#64748b",
  lineHeight: 1.45
}

export const closeButtonStyle = {
  minWidth: 36,
  minHeight: 36,
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: "#334155",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer"
}

export const modalActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 16
}